let widgetPromise = null;

const CAP_WASM_URL = '/assets/cap/cap_wasm_bg.wasm';
const CAP_PAKO_URL = '/assets/cap/pako_inflate.min.js';
const CAP_SOLVE_TIMEOUT_MS = 10000;

const normalise = (value) => typeof value === 'string' ? value.trim() : '';

const ensureHiddenInput = (formElement, name) => {
    if (!formElement) return null;

    let input = formElement.querySelector(`input[name="${name}"]`);
    if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        formElement.appendChild(input);
    }

    return input;
};

const setInputValue = (input, value) => {
    if (!input) return;
    input.value = value || '';
};

const loadCapWidget = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(null);
    }

    if (typeof window.Cap === 'function') {
        return Promise.resolve(window.Cap);
    }

    if (widgetPromise) {
        return widgetPromise;
    }

    widgetPromise = Promise.resolve().then(() => {
        // Keep this require inside the browser-only path. The package reads browser globals when loaded,
        // while Velocity also imports this module during server-side rendering.
        window.CAP_CUSTOM_WASM_URL = window.CAP_CUSTOM_WASM_URL || CAP_WASM_URL;
        window.CAP_PAKO_URL = window.CAP_PAKO_URL || CAP_PAKO_URL;
        const capModule = require('@cap.js/widget');
        const Cap = capModule?.default || capModule;
        if (typeof Cap !== 'function') {
            throw new Error('Cap programmatic API is unavailable');
        }

        window.Cap = window.Cap || Cap;
        return window.Cap;
    }).catch((error) => {
        widgetPromise = null;
        throw error;
    });

    return widgetPromise;
};

const createCapController = ({formElement, containerElement, apiEndpoint, onStateChange} = {}) => {
    const resolvedApiEndpoint = normalise(apiEndpoint);
    const providerTokenInput = ensureHiddenInput(formElement, 'cap-token');
    const tokenInput = ensureHiddenInput(formElement, 'captchaToken');
    const providerInput = ensureHiddenInput(formElement, 'captchaProvider');
    const statusInput = ensureHiddenInput(formElement, 'captchaStatus');

    let capInstance = null;
    let destroyed = false;
    let loadFailed = false;
    let currentToken = '';
    let currentStatus = '';
    let execution = null;
    let generation = 0;

    setInputValue(providerInput, resolvedApiEndpoint ? 'cap' : '');

    const emitStateChange = () => {
        if (typeof onStateChange === 'function') {
            onStateChange({
                token: currentToken,
                status: currentStatus,
                ready: !!capInstance,
                loadFailed,
            });
        }
    };

    const syncState = ({token = currentToken, status = currentStatus} = {}) => {
        currentToken = token || '';
        currentStatus = status || '';
        setInputValue(providerTokenInput, currentToken);
        setInputValue(tokenInput, currentToken);
        setInputValue(statusInput, currentStatus);
        setInputValue(providerInput, resolvedApiEndpoint ? 'cap' : '');
        emitStateChange();
    };

    const readToken = (instance, event) => {
        return event?.detail?.token
            || event?.detail?.response
            || instance?.token
            || instance?.widget?.token
            || instance?.widget?.value
            || '';
    };

    const discardInstance = () => {
        const instance = capInstance;
        capInstance = null;
        generation += 1;
        // The pinned widget aborts requests and cleans up workers on disconnect.
        // reset() alone only clears its token and does not cancel a solve.
        instance?.widget?.remove?.();
    };

    const finishExecution = (attempt, token, status, discard = false) => {
        if (execution !== attempt) return;
        execution = null;
        clearTimeout(attempt.timer);
        if (discard) discardInstance();
        syncState({token: token || '', status});
        attempt.resolve(token || null);
    };

    const initProgrammaticCap = async () => {
        if (destroyed || !formElement || !resolvedApiEndpoint) {
            syncState({token: '', status: ''});
            return false;
        }

        const initialGeneration = generation;
        try {
            const Cap = await loadCapWidget();
            if (destroyed || generation !== initialGeneration) return false;

            if (typeof Cap !== 'function') {
                throw new Error('Cap programmatic API is unavailable');
            }

            if (!capInstance) {
                const instance = new Cap({apiEndpoint: resolvedApiEndpoint});
                capInstance = instance;
                instance.addEventListener('solve', (event) => {
                    if (destroyed || capInstance !== instance || execution?.instance !== instance) return;
                    const token = readToken(instance, event);
                    syncState({token, status: token ? 'valid' : ''});
                });
                instance.addEventListener('error', () => {
                    if (destroyed || capInstance !== instance || execution?.instance !== instance) return;
                    finishExecution(execution, null, 'error', true);
                });
            }

            emitStateChange();
            return true;
        } catch (error) {
            if (destroyed || generation !== initialGeneration) return false;
            loadFailed = true;
            emitStateChange();
            return false;
        }
    };

    return {
        init: () => initProgrammaticCap(),
        execute: () => {
            if (destroyed) return Promise.resolve(null);
            if (execution) return execution.promise;
            if (currentToken) return Promise.resolve(currentToken);

            const attempt = {};
            attempt.promise = new Promise(resolve => {attempt.resolve = resolve;});
            execution = attempt;
            attempt.timer = setTimeout(() => {
                finishExecution(attempt, null, 'timeout', true);
            }, CAP_SOLVE_TIMEOUT_MS);

            (async () => {
                try {
                    const ready = capInstance || await initProgrammaticCap();
                    if (execution !== attempt) return;
                    if (!ready || !capInstance || typeof capInstance.solve !== 'function') {
                        finishExecution(attempt, null, 'error', true);
                        return;
                    }

                    const instance = capInstance;
                    attempt.instance = instance;
                    syncState({token: '', status: 'verifying'});
                    const result = await instance.solve();
                    if (execution !== attempt) return;
                    const token = result?.token || readToken(instance) || '';
                    finishExecution(attempt, token, token ? 'valid' : 'error', !token);
                } catch (error) {
                    finishExecution(attempt, null, 'error', true);
                }
            })();

            return attempt.promise;
        },
        reset: () => {
            if (execution) {
                finishExecution(execution, null, '', true);
            } else {
                syncState({token: '', status: ''});
                if (capInstance && typeof capInstance.reset === 'function') capInstance.reset();
            }
        },
        destroy: () => {
            destroyed = true;
            if (execution) finishExecution(execution, null, '', true);
            else {
                discardInstance();
                syncState({token: '', status: ''});
            }
        },
        getToken: () => currentToken,
        getStatus: () => currentStatus,
        isConfigured: () => resolvedApiEndpoint.length > 0,
        isReady: () => !!capInstance,
        hasLoadFailure: () => loadFailed,
    };
};

module.exports = {
    createCapController,
    loadCapWidget,
};
module.exports.default = module.exports;
